import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    SerializedError,
    SliceCaseReducers,
} from '@reduxjs/toolkit';
import { urlUserLogout, urlUserProfile } from '../../Constants';
import { IAction, IAppReduxStore, SliceProps, SlicePropsWithData } from '../store';

/**
 * Application backend writes this variable to the page on load.
 * This global variable contains the URL that is used to log the user out of the application in Auth0.
 */
declare const auth0LogoutUri: string;

/**
 * Initial slice state value.
 */
const initialState: IUserProfileState = {
    data: null,
    loading: false,
    error: null,
    picture: {
        loading: false,
        error: null,
    },
    maxProfilePictureSize: 2621440,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's settings from the backend.
     */
    getUserProfile: async (): Promise<IUserData> => {
        const response: Response = await fetch(urlUserProfile);

        const content = await response.json();

        return response.ok ? content : Promise.reject(content?.message || response.status);
    },

    /**
     * Logs the user out of the application.
     */
    logoutUser: async (): Promise<void> => {
        const response = await fetch(urlUserLogout, { method: 'POST' });

        return response.ok ? Promise.resolve() : Promise.reject(response.status);
    },

    /**
     * Updates the user's picture in the backend.
     */
    updateUserProfilePicture: async (image: File, thunkApi): Promise<string> => {
        /** Note: Backend only accepts data in form-format! */

        if (
            image.size > (thunkApi.getState() as IAppReduxStore).userProfile.maxProfilePictureSize
        ) {
            return Promise.reject('Failed to upload image, image size is more than 2,5MB.');
        }
        const data: FormData = new FormData();

        data.append('image', image);

        try {
            const res = await fetch(`${urlUserProfile}/image`, { method: 'POST', body: data });
            const pictureData = await res.text();
            return pictureData ? pictureData : Promise.reject('No picture data.');
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserProfileState>) => {
        const onLoad = (state: IUserProfileState) => {
            state.error = null;
            state.loading = true;
        };

        const onError = (state: IUserProfileState, action: IAction<SerializedError>) => {
            state.loading = false;
            state.data = null;
            state.error = action.error;
        };

        /**
         * Reducers for the 'getUserProfile' thunk.
         */
        builder.addCase(getUserProfile.pending, onLoad);
        builder.addCase(
            getUserProfile.fulfilled,
            (state: IUserProfileState, action: IAction<IUserData>) => {
                state.data = action.payload;
                state.error = null;
                state.loading = false;
            },
        );
        builder.addCase(getUserProfile.rejected, onError);

        /**
         * Reducers for the 'logoutUser' thunk.
         */
        builder.addCase(logoutUser.pending, onLoad);
        builder.addCase(logoutUser.fulfilled, () => {
            window.location.href = auth0LogoutUri;
        });
        builder.addCase(logoutUser.rejected, onError);

        /**
         * Reducers for the 'updateUserProfilePicture' thunk.
         */
        builder.addCase(updateUserProfilePicture.pending, (state: IUserProfileState) => {
            state.picture.loading = true;
        });
        builder.addCase(
            updateUserProfilePicture.fulfilled,
            (state: IUserProfileState, action: IAction<string>) => {
                state.data.picture = action.payload;
                state.picture.loading = false;
            },
        );
        builder.addCase(
            updateUserProfilePicture.rejected,
            (state: IUserProfileState, action: IAction<SerializedError>) => {
                state.picture.error = action.error;
                state.picture.loading = false;
            },
        );
    },
};

/**
 * Interface that describes the state object for this redux slice.
 */
export type IUserProfileState = SlicePropsWithData<IUserData> & {
    /** Max size in Bytes of image that is allowed for downloading. */
    maxProfilePictureSize: number;

    /** Defines loading and error state for the user avatar. */
    picture: SliceProps;
};

/**
 * Interface that describes the metadata returned from Auth0 for the user.
 */
export interface IUserData {
    /** Custom fields that store info about a user that influences the user's access. */
    app_metadata: unknown;

    /** Indicates whether the user has been blocked. */
    blocked: boolean;

    /** Timestamp indicating when the user profile was first created. */
    created_at: string;

    /** (unique) The user's email address. */
    email: string;

    /** Indicates whether the user has verified their email address. */
    email_verified: boolean;

    /** The user's family name. */
    family_name: string;

    /** The user's given name. */
    given_name: string;

    /** Custom fields that store info about a user that does not impact what they can or cannot access. */
    user_metadata: unknown;

    /** (unique) The user's username. */
    username: string;

    /** The user's first_name. */
    first_name: string;

    /** The user's last_name. */
    last_name: string;

    /** Permission to use information. */
    dataprotection: string;

    /** Determines if the user is a shared system user. */
    isSystemuser: boolean;

    /** Contains info retrieved from the identity provider with which the user originally authenticates. */
    identities: {
        /** Name of the Auth0 connection used to authenticate the user. */
        connection: string;

        /** Indicates whether the connection is a social one. */
        isSocial: boolean;

        /** Name of the entity that is authenticating the user, such as Facebook, Google, SAML, or your own provider. */
        provider: string;

        /** User's unique identifier for this connection/provider. */
        user_id: string;

        /** User info associated with the connection. */
        profileData: unknown;
    }[];

    /** IP address associated with the user's last login. */
    last_ip: string;

    /** Timestamp indicating when the user last logged in. If a user is blocked and logs in, the blocked session updates last_login. */
    last_login: string;

    /** Timestamp indicating the last time the user's password was reset/changed. */
    last_password_reset: string;

    /** Number of times the user has logged in. */
    logins_count: number;

    /** List of multi-factor providers with which the user is enrolled. */
    multifactor: string;

    /** The user's full name. */
    name: string;

    /** The user's nickname. */
    nickname: string;

    /** User's notification days to receive an email. */
    notificationDays: number[];

    /** The user's phone number. Only valid for users with SMS connections. */
    phone_number: string;

    /** Indicates whether the user has been verified their phone number. Only valid for users with SMS connections. */
    phone_verified: boolean;

    /** URL pointing to the user's profile picture. */
    picture: string;

    /** Timestamp indicating when the user's profile was last updated/modified. */
    updated_at: string;

    /** (unique) The user's identifier. */
    user_id: string;
}

/**
 * Expose thunk functions as exports.
 */
export const getUserProfile = createAsyncThunk(`user/get`, thunks.getUserProfile);
export const logoutUser = createAsyncThunk(`user/logout`, thunks.logoutUser);
export const updateUserProfilePicture = createAsyncThunk(
    `${urlUserProfile}/image`,
    thunks.updateUserProfilePicture,
);

/**
 * Create the redux slice.
 */
export const userProfileSlice = createSlice<
    IUserProfileState,
    SliceCaseReducers<IUserProfileState>
>({
    name: 'userProfile',
    initialState: initialState,
    reducers: {},
    extraReducers: thunks._reducers,
});

/**
 * Export slice reducer as default.
 */
export default userProfileSlice.reducer;
